<script setup lang="ts">
import Translator from "@/utils/common/Translator.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import {useQuestionList} from "./useQuestionList.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import QuestionField = namaTypes.QuestionField;

const props = defineProps<{
  presenter: QuestionFieldsPresenter, questionField: QuestionField,
  doNotApplyThemeLayouts: boolean
}>()

const {
  selectedDetails, selectedRowsNumber, getDetailText, removeDetail,
  searchText, isLoading, displayedItems, fieldTitle,
  isItemSelected, allSelected, someSelected,
  toggleAll, toggleItem, onPlusClick, searchRef,
} = useQuestionList(props.presenter, props.questionField);
</script>

<template>
  <WidgetContainer
    :field="questionField"
    :entityType="presenter.entityType"
    :isFieldDisabled="false"
    :isFieldRequired="questionField.required"
    :doNotApplyThemeLayouts="doNotApplyThemeLayouts"
    :isInsideComposite="false">
    <template #icon>
      <NamaIcon :block="questionField" :presenter="presenter" class="tw-size-[22px] tw-text-[22px]"/>
    </template>
    <template v-slot:widget>
      <q-input outlined dense hide-bottom-space
               :id="questionField.id"
               autocomplete="off"
               readonly
               class="nama-customize-dense-input nama-focused-input nama-field-question-list"
               input-class="nama-input-class"
               :model-value="''">
        <template v-slot:prepend>
          <div class="tw-flex tw-flex-wrap tw-gap-1 tw-items-center tw-p-1 tw-justify-between tw-w-full">
            <div class="tw-flex items-center tw-flex-wrap tw-gap-1">
              <q-chip
                v-for="detail in selectedDetails"
                :key="detail.id"
                removable
                size="sm"
                @remove="removeDetail(detail)">
                <span class="tw-text-xs" v-text="getDetailText(detail)"/>
              </q-chip>
            </div>
            <button type="button"
                    :title="Translator.translateId('select')"
                    @click.stop.prevent="onPlusClick"
                    class="tw-group tw-flex tw-items-center tw-gap-1 tw-h-6 tw-px-2 tw-rounded-full
                           tw-border tw-border-black/10 dark:tw-border-white/15
                           tw-bg-white/60 dark:tw-bg-white/5
                           hover:tw-bg-primary-light hover:tw-text-white hover:tw-border-transparent
                           dark:hover:tw-bg-primary-dark
                           hover:tw-shadow-sm
                           tw-text-namaFontColor-light dark:tw-text-namaFontColor-dark
                           tw-transition-all tw-duration-150 tw-cursor-pointer">
              <span v-if="selectedRowsNumber"
                    class="tw-text-[11px] tw-font-semibold tw-leading-none tw-tabular-nums"
                    v-text="selectedRowsNumber"/>
              <q-icon name="add" size="14px" class="tw-transition-transform group-hover:tw-rotate-90"/>
              <q-menu anchor="bottom end"
                      self="top end"
                      class="tw-min-w-[260px] !tw-max-h-[45vh] tw-rounded-md tw-shadow-lg
                             tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark"
                      @click.stop>
                <div class="tw-sticky tw-top-0 tw-z-10 tw-p-2 tw-border-b tw-border-black/10 dark:tw-border-white/10
                            tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark">
                  <q-input ref="searchRef"
                           v-model="searchText"
                           dense outlined
                           clearable
                           autofocus
                           :placeholder="Translator.translateId('search')"
                           :loading="isLoading"
                           class="nama-customize-dense-input">
                    <template v-slot:prepend>
                      <q-icon name="search" size="16px"/>
                    </template>
                  </q-input>
                </div>
                <div class="tw-flex tw-items-center tw-gap-2 tw-px-1 tw-py-1 tw-border-b tw-border-black/5 dark:tw-border-white/10
                            tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-black/45 dark:tw-text-white/45">
                  <q-checkbox
                    :dark="useNamaFlagsStore().darkMode"
                    :model-value="allSelected ? true : someSelected ? null : false"
                    :indeterminate-value="null"
                    :toggle-indeterminate="false"
                    @click.stop.prevent="toggleAll"
                    dense size="sm"
                    class="tw-shrink-0"/>
                  <span class="tw-w-[90px] tw-shrink-0" v-text="Translator.translateId('code')"/>
                  <span v-text="fieldTitle"/>
                </div>
                <q-list dense>
                  <q-item
                    v-for="item in displayedItems"
                    :key="item.id"
                    clickable
                    @click="toggleItem(item)">
                    <q-item-section side class="!tw-pr-0 !tw-min-w-0">
                      <q-checkbox
                        :dark="useNamaFlagsStore().darkMode"
                        :model-value="isItemSelected(item)"
                        @click.stop.prevent="toggleItem(item)"
                        dense size="sm"/>
                    </q-item-section>
                    <q-item-section class="!tw-max-w-[90px] !tw-min-w-[90px]">
                      <q-item-label class="tw-text-xs tw-font-semibold tw-truncate" v-text="item.code"/>
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="tw-text-xs tw-text-black/70 dark:tw-text-white/70 tw-truncate" v-text="item.caption"/>
                    </q-item-section>
                  </q-item>
                  <q-item v-if="!displayedItems.length && !isLoading">
                    <q-item-section>
                      <q-item-label class="dark:tw-text-white/50" caption v-text="Translator.translateId('noResults')"/>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </button>
          </div>
        </template>
      </q-input>
    </template>
  </WidgetContainer>
</template>
